import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  applyIfLatest,
  latestRequestInitialState,
  trackLatestRequests,
} from 'state/latestRequest';

import { LsKeys } from 'enums';
import {
  createNotification,
  getNotifications,
} from 'state/thunks/notificationThunk/notificationThunk';
import {
  closeUserAccount,
  editUser,
  getPatientVisits,
  getUser,
  getUsers,
} from 'state/thunks/userThunk/userThunk';

const initialState = {
  users: {},
  count: null,
  lastPage: null,
  page: null,
  user: null,
  isLoading: false,
  visits: [],
  /**
   * Closing an account is its own in-flight flag, not `isLoading`.
   *
   * `isLoading` puts a full-page spinner over both detail pages, which would
   * take the confirmation dialog -- and the refusal it has to be able to show
   * -- off the screen the moment the request went out.
   */
  isClosingAccount: false,
  closeAccountError: '' as string,
  /**
   * The status dropdown's own in-flight flag and error slot.
   *
   * Separate from `isLoading` for the same reason `isClosingAccount` is: that
   * flag paints a full-page spinner over the whole detail page, which is far
   * too much for a one-field PATCH and would take the error message off screen
   * at the moment it arrived.
   *
   * Before this existed the rejection was discarded outright -- `editUser`'s
   * rejected case set `isLoading = false` and dropped the payload -- so a
   * refused or dropped status change left the dropdown reading the status the
   * operator chose, in colour, over an account the server had not changed.
   */
  isSavingStatus: false,
  statusError: '' as string,
  ...latestRequestInitialState,
};

/**
 * The filtered list read is guarded by `applyIfLatest`: a search string, a
 * status filter and a page all change it, so overlapping requests are routine
 * and the reducer must apply the newest REQUEST rather than the newest
 * response. See src/state/latestRequest.ts.
 */
const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLogOut: () => initialState,
    clearCloseAccountError: state => {
      state.closeAccountError = '';
    },
    clearStatusError: state => {
      state.statusError = '';
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<any>) => {
    builder
      .addCase(getPatientVisits.fulfilled, (state, action: PayloadAction<any>) => {
        console.log('get user success');
        console.log(action.payload);
        console.log(action.payload.rows);
        state.visits = action.payload;
      })
      /*
       * The detail pages' spinner tracks the PROFILE READ, which is what it
       * always should have tracked.
       *
       * `isLoading` was raised only by `editUser`, so the full-page spinner
       * appeared on a one-field status PATCH -- blanking the profile the
       * operator was reading, and taking the refusal message off screen at the
       * moment it arrived -- while the initial load, the one moment there is
       * genuinely nothing to show, had no spinner at all. Moving the flag onto
       * `getUser` swaps those the right way round. `editUser` now has its own
       * `isSavingStatus`, which only disables the dropdown.
       */
      .addCase(getUser.pending, state => {
        state.isLoading = true;
      })
      .addCase(getUser.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(
        getUsers.fulfilled,
        applyIfLatest(getUsers, (state, payload: any) => {
        console.log('get user success');
        console.log(payload);
        console.log(payload.rows);
        state.users = payload;
        state.count = payload.count;
        state.lastPage = payload.lastPage;
        state.page = payload.page;
        }),
      )
      // `isLoading` is deliberately no longer touched here. It draws a
      // full-page spinner, and a one-field PATCH does not warrant blanking the
      // profile the operator is reading -- it also hid the refusal below.
      .addCase(editUser.pending, state => {
        state.isSavingStatus = true;
        state.statusError = '';
      })
      .addCase(editUser.fulfilled, state => {
        state.isSavingStatus = false;
      })
      .addCase(editUser.rejected, (state, action: PayloadAction<any>) => {
        state.isSavingStatus = false;
        // Never left empty, for the same reason `closeAccountError` is not: a
        // rejection that did not come through `rejectWithValue` has an
        // undefined payload, and an empty message renders as a control that
        // did nothing.
        state.statusError =
          action.payload || 'Could not change the status. Please try again.';
      })
      .addCase(closeUserAccount.pending, state => {
        state.isClosingAccount = true;
        state.closeAccountError = '';
      })
      .addCase(closeUserAccount.fulfilled, state => {
        state.isClosingAccount = false;
      })
      .addCase(closeUserAccount.rejected, (state, action: PayloadAction<any>) => {
        state.isClosingAccount = false;
        // Never left empty. The dialog shows this slot and nothing else, so an
        // undefined payload -- a rejection that did not come through
        // rejectWithValue -- would read as a button that did nothing.
        state.closeAccountError = action.payload || 'Could not close the account.';
      })
      .addCase(getUsers.rejected, (state, action: PayloadAction<any>) => {
        //state.error = action.payload;
        //state.isLoading = false;
      });

    // Matchers must come after every addCase.
    trackLatestRequests(builder, [getUsers]);
  },
});

export const userActions = slice.actions;

export const userReducer = slice.reducer;
